import { useForYouChats } from '@renderer/overlay/hooks/use-running-chats'
import { useStagedActionJobs } from '@renderer/overlay/hooks/use-staged-action-jobs'
import { useCanDraftFollowUps, useMeetingFollowUps } from '../use-meeting-follow-ups'
import { isMeetingPlannerRunning } from '../meeting-follow-ups'
import { PlannerDebugSection } from '../planner-debug/planner-debug-section'
import { usePlannerDebugAccess } from '../planner-debug/use-planner-debug-access'
import { MeetingViewForYou, type MeetingFollowUpAppearance } from './follow-ups'
import { MeetingViewSummarySection } from './summary-section'
import type { MeetingSummaryState } from '../derive-meeting-summary-state'
import type { MeetingViewModel } from '../use-meeting-view-model'

interface MeetingViewRecapPanelProps {
  /** The open tab, passed down so only the panel on screen offers its copy. */
  isActiveTab: boolean
  model: MeetingViewModel
  summary: MeetingSummaryState
  followUpAppearance: MeetingFollowUpAppearance
}

/**
 * The Recap tab: For You over the Summary. Follow-ups lead because they're the
 * actionable part, and the summary can be arbitrarily long. After it they'd
 * live below the fold.
 *
 * Owns the follow-up queries. The panel unmounts with the tab, so a meeting
 * opened on its transcript doesn't pay for them.
 */
export function MeetingViewRecapPanel({ model, summary, followUpAppearance, isActiveTab }: MeetingViewRecapPanelProps) {
  const plannerDebugEnabled = usePlannerDebugAccess()
  const followUps = useMeetingFollowUps(model.audioNote?.id)
  const forYouChats = useForYouChats({
    audioNoteId: model.audioNoteId,
    audioSessionId: model.audioSessionId,
  })
  const { jobs: stagedActionJobs } = useStagedActionJobs()
  const isPlannerRunning = isMeetingPlannerRunning(stagedActionJobs, {
    audioNoteId: model.audioNoteId,
    audioSessionId: model.audioSessionId,
  })
  const canDraftFollowUps = useCanDraftFollowUps()

  return (
    <div className="flex flex-col">
      <MeetingViewForYou
        chats={forYouChats}
        followUps={followUps}
        phase={summary.phase}
        canDraftFollowUps={canDraftFollowUps}
        isPlannerRunning={isPlannerRunning}
        followUpAppearance={followUpAppearance}
      />
      <MeetingViewSummarySection summary={summary} audioNoteId={model.audioNoteId} isActiveTab={isActiveTab} />
      {plannerDebugEnabled && model.audioNoteId && <PlannerDebugSection noteId={model.audioNoteId} />}
    </div>
  )
}
